/*
 * The keys of a review's Code layer (S14 §11, `docs/web/keyboard.md` § In a review's code), in the
 * `diff` scope, which the Code layer pushes while it is on screen. Like the chat page's keys
 * (`../shortcuts.ts`) a definition is not a behaviour: each binding calls the controller the open
 * Code layer installs, and with none installed the scope is not on the stack. `?` is not here: it is
 * the global keyboard sheet, which lists these keys under their own heading. Every binding is
 * remappable.
 *
 * This file is reachable from the entry chunk through the module's shortcut slot, so it imports
 * types and nothing else.
 */

import type { ShortcutDef } from "../../../core/modules/registry.ts"

/** What the open Code layer lends its keys. */
export interface DiffController {
	/** `j` / `k`: the next or previous file. */
	stepFile(dir: 1 | -1): void
	/** `n` / `p`: the next or previous change; `F7` / `Shift+F7` also read it out. */
	stepChange(dir: 1 | -1, speak: boolean): void
	/** `s`: split or unified, remembered. */
	toggleView(): void
	/** `w`: whitespace shown or ignored. */
	toggleWhitespace(): void
	/** `a`: ask Claude about the selected lines, or select the change the keyboard stands on. */
	ask(): void
	/** `t`: to the file tree. */
	toTree(): void
	/** `[` / `]`: the previous or next revision. */
	stepRevision(dir: 1 | -1): void
}

let controller: DiffController | null = null

/** Installed by the Code layer while it is mounted; cleared when it is not. */
export function setDiffController(next: DiffController | null): void {
	controller = next
}

function key(
	id: string,
	keys: string[],
	labelKey: string,
	run: (c: DiffController) => void,
): ShortcutDef {
	return {
		id: `aichat.review.${id}`,
		keys,
		scope: "diff",
		labelKey,
		remappable: true,
		run: () => controller && run(controller),
	}
}

export const reviewShortcuts: ShortcutDef[] = [
	key("nextFile", ["j"], "aichat.review.keys.nextFile", (c) => c.stepFile(1)),
	key("previousFile", ["k"], "aichat.review.keys.previousFile", (c) => c.stepFile(-1)),
	key("nextChange", ["n"], "aichat.review.keys.nextChange", (c) => c.stepChange(1, false)),
	key("previousChange", ["p"], "aichat.review.keys.previousChange", (c) => c.stepChange(-1, false)),
	key("readNextChange", ["F7"], "aichat.review.keys.readNextChange", (c) => c.stepChange(1, true)),
	key("readPreviousChange", ["Shift+F7"], "aichat.review.keys.readPreviousChange", (c) =>
		c.stepChange(-1, true),
	),
	key("toggleView", ["s"], "aichat.review.keys.toggleView", (c) => c.toggleView()),
	key("toggleWhitespace", ["w"], "aichat.review.keys.toggleWhitespace", (c) =>
		c.toggleWhitespace(),
	),
	key("ask", ["a"], "aichat.review.keys.ask", (c) => c.ask()),
	key("toTree", ["t"], "aichat.review.keys.toTree", (c) => c.toTree()),
	key("previousRevision", ["["], "aichat.review.keys.previousRevision", (c) => c.stepRevision(-1)),
	key("nextRevision", ["]"], "aichat.review.keys.nextRevision", (c) => c.stepRevision(1)),
]
